/**
 * readAloudCopy — the words on the readout player, and the one line it leaves
 * behind when a readout drops out mid-sentence.
 *
 * D9 (granted exception, 2026-08-24): code may author FAILURE copy and chrome —
 * never content. The player's buttons are chrome; the dropped-out line is a
 * failure notice, deliberately deterministic because when it fires the voice
 * lane is the broken part. It matches the server's own inventory
 * (functions/src/prest0nSession/voiceCopy.ts) in voice and shape: lowercase,
 * warm, no jargon, no vendor, no error code, and the reader's own language.
 *
 * Pure and IO-free apart from the language lookup.
 */

import { getUserUiLanguage } from '../threadMetadata/uiLanguage';

export type ReadAloudLang = 'en' | 'es';

export interface ReadAloudPlayerCopy {
  previous: string;
  next: string;
  pause: string;
  play: string;
  restart: string;
  slower: string;
  faster: string;
  speed: string;
  /** "2 / 5" — which spoken section of the message is sounding. */
  position: (segment: number, total: number) => string;
  /** "120 %" — the speed, in the reader's own units. */
  ratePercent: (rate: number) => string;
  player: string;
}

const EN: ReadAloudPlayerCopy = {
  previous: 'previous section',
  next: 'next section',
  pause: 'pause',
  play: 'play',
  restart: 'start over',
  slower: 'slower',
  faster: 'faster',
  speed: 'speed',
  position: (segment, total) => `${segment} / ${total}`,
  ratePercent: (rate) => `${Math.round(rate * 100)} %`,
  player: 'reading controls',
};

const ES: ReadAloudPlayerCopy = {
  previous: 'sección anterior',
  next: 'sección siguiente',
  pause: 'pausar',
  play: 'reproducir',
  restart: 'empezar de nuevo',
  slower: 'más lento',
  faster: 'más rápido',
  speed: 'velocidad',
  position: (segment, total) => `${segment} / ${total}`,
  ratePercent: (rate) => `${Math.round(rate * 100)} %`,
  player: 'controles de lectura',
};

export function readAloudLang(language?: string | null): ReadAloudLang {
  const raw = String(language || '').trim().toLowerCase();
  if (raw.startsWith('es')) return 'es';
  if (raw.startsWith('en')) return 'en';
  return getUserUiLanguage() === 'es' ? 'es' : 'en';
}

export function readAloudPlayerCopy(language?: string | null): ReadAloudPlayerCopy {
  return readAloudLang(language) === 'es' ? ES : EN;
}

/**
 * The readout was going and then it was not — a segment's audio failed twice,
 * a signed url went stale, a socket dropped. Says what happened and that trying
 * again is worth it, which is true: the render is cached, so the second attempt
 * is usually instant and free.
 */
export function readAloudPlaybackFailedNotice(language?: string | null): string {
  return readAloudLang(language) === 'es'
    ? 'la voz se cortó a mitad. inténtalo otra vez en un momento.'
    : 'the voice dropped out partway through. try again in a moment.';
}
